"use client";

import { useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import { PageWrapper } from "ui/components";

import { AppShell } from "../AppShell";
import { Layout } from "../Layout";
import { useWeb3 } from "../providers";

interface LoansChromeProps {
  children: ReactNode;
}

/**
 * Picks the loans route's chrome from `?loan=`: the reskinned `AppShell` for the
 * list, the old `Layout` for the detail view, which has not been reskinned and
 * is built expecting that chrome plus `PageWrapper`.
 *
 * A client component rather than the layout itself because Next layouts are
 * per-path-segment — they receive no search params and do not re-render when the
 * query changes, so `?loan=` cannot select a layout the usual way. The
 * alternative is moving the detail view to its own path, which would mean
 * reworking how it selects and navigates loans; that waits for its reskin.
 *
 * Switching branches swaps the wrapper, so the page subtree remounts and
 * `LoansPageProvider` re-runs the loans query — served from cache, refetched
 * behind it.
 *
 * With no wallet connected the page is the full-screen `BorrowerLogin`, so it
 * gets no chrome at all.
 */
export function LoansChrome(props: LoansChromeProps) {
  const { children } = props;

  const isDetail = Boolean(useSearchParams().get("loan"));
  const {
    account: { ready, address },
  } = useWeb3();

  if (ready && !address) return <>{children}</>;

  if (isDetail)
    return (
      <Layout>
        <PageWrapper>{children}</PageWrapper>
      </Layout>
    );

  return <AppShell>{children}</AppShell>;
}
